import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Star,
  Bookmark as BookmarkIcon,
  Download,
  Folder,
  FolderPlus,
  Pencil,
  Trash2,
  Check,
  X,
  Move,
} from 'lucide-react'
import { bookmarkApi, type Bookmark, type BookmarkFolder } from '../api/bookmarks'
import ResultsTable from '../components/ResultsTable'
import Popover from '../components/Popover'
import { downloadCSV } from '../utils/csv'

type FolderFilter = { kind: 'all' } | { kind: 'unsorted' } | { kind: 'folder'; id: string }

export default function BookmarksPage() {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([])
  const [folders, setFolders] = useState<BookmarkFolder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FolderFilter>({ kind: 'all' })
  const [newFolderMode, setNewFolderMode] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')
  const [movePmid, setMovePmid] = useState<string | null>(null)
  const [moveAnchor, setMoveAnchor] = useState<HTMLElement | null>(null)
  const navigate = useNavigate()

  const loadFolders = async () => {
    try {
      const folderList = await bookmarkApi.listFolders()
      setFolders(folderList)
    } catch (err) {
      console.error('Failed to load folders:', err)
    }
  }

  const loadBookmarks = async (currentFilter: FolderFilter = filter) => {
    try {
      setLoading(true)
      const folderParam =
        currentFilter.kind === 'all'
          ? undefined
          : currentFilter.kind === 'unsorted'
            ? 'unsorted'
            : currentFilter.id
      const data = await bookmarkApi.listBookmarks(folderParam)
      setBookmarks(data)
      setError(null)
    } catch (err) {
      setError('Failed to load bookmarks')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFolders()
  }, [])

  useEffect(() => {
    loadBookmarks(filter)
  }, [filter])

  const totalCount = folders.reduce((acc, f) => acc + (f.bookmark_count || 0), 0)
  // unsorted count cannot be inferred from folders alone — we'll compute on-the-fly
  // when filter is 'all' by counting bookmarks with folder_id null. For sidebar display
  // we only show counts that we know reliably.
  const unsortedCountFromAll =
    filter.kind === 'all' ? bookmarks.filter(b => !b.folder_id).length : null

  const handleCreateFolder = async () => {
    const name = newFolderName.trim()
    if (!name) return
    try {
      await bookmarkApi.createFolder({ name })
      setNewFolderName('')
      setNewFolderMode(false)
      await loadFolders()
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to create folder')
    }
  }

  const handleRename = async (folderId: string) => {
    const name = renameDraft.trim()
    if (!name) return
    try {
      await bookmarkApi.updateFolder(folderId, { name })
      setRenamingId(null)
      setRenameDraft('')
      await loadFolders()
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to rename folder')
    }
  }

  const handleDeleteFolder = async (folder: BookmarkFolder) => {
    if (!window.confirm(
      `Delete folder "${folder.name}"? Its ${folder.bookmark_count || 0} bookmark(s) will move to Unsorted.`
    )) return
    try {
      await bookmarkApi.deleteFolder(folder.folder_id)
      if (filter.kind === 'folder' && filter.id === folder.folder_id) {
        setFilter({ kind: 'all' })
      }
      await loadFolders()
      await loadBookmarks()
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to delete folder')
    }
  }

  const handleMove = async (pmid: string, folderId: string | null) => {
    try {
      await bookmarkApi.moveBookmark(pmid, folderId)
      setMovePmid(null)
      setMoveAnchor(null)
      await loadFolders()
      await loadBookmarks()
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to move bookmark')
    }
  }

  const handleBookmarkToggle = (pmid: string, isBookmarked: boolean) => {
    if (!isBookmarked) {
      setBookmarks(prev => prev.filter(b => b.pmid !== pmid))
      loadFolders()
    }
  }

  // Map bookmarks to article format for ResultsTable + add a Move button
  const articles = bookmarks.map(b => ({
    pmid: b.pmid,
    title: b.article?.title,
    authors: b.article?.authors,
    authors_full: b.article?.authors?.join(', '),
    journal: b.article?.journal,
    year: b.article?.year,
    abstract: b.article?.abstract,
    quartile: b.article?.quartile,
    sjr_score: b.article?.sjr_score,
    citations: b.article?.citations,
    rcr: b.article?.rcr,
    citations_per_year: b.article?.citations_per_year,
    field_citation_rate: b.article?.field_citation_rate,
    nih_percentile: b.article?.nih_percentile,
    doi: b.article?.doi,
    mesh_terms: b.article?.mesh_terms,
    keywords: b.article?.keywords,
    affiliations: b.article?.affiliations,
    author_emails: b.article?.author_emails,
  }))

  const moveButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <BookmarkIcon className="w-6 h-6 text-brand-600" />
        <h1 className="text-2xl font-bold text-gray-900">Bookmarked Articles</h1>
        <span className="bg-brand-100 text-brand-700 px-2.5 py-0.5 rounded-full text-sm font-medium">
          {bookmarks.length}
        </span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <div className="flex gap-4">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 bg-white border rounded-lg p-3 h-fit">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Folders
          </div>
          <button
            onClick={() => setFilter({ kind: 'all' })}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left mb-0.5 ${
              filter.kind === 'all' ? 'bg-brand-50 text-brand-700' : 'hover:bg-gray-50'
            }`}
          >
            <Folder className="w-3.5 h-3.5 text-gray-500" />
            <span className="flex-1">All</span>
            <span className="text-[10px] text-gray-400">{totalCount + (unsortedCountFromAll ?? 0)}</span>
          </button>
          <button
            onClick={() => setFilter({ kind: 'unsorted' })}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left mb-0.5 ${
              filter.kind === 'unsorted' ? 'bg-brand-50 text-brand-700' : 'hover:bg-gray-50'
            }`}
          >
            <Folder className="w-3.5 h-3.5 text-gray-300" />
            <span className="flex-1 italic text-gray-600">Unsorted</span>
          </button>

          <div className="my-2 border-t" />

          {folders.map(f => (
            <div
              key={f.folder_id}
              className={`group flex items-center gap-1 px-2 py-1 rounded text-sm mb-0.5 ${
                filter.kind === 'folder' && filter.id === f.folder_id
                  ? 'bg-brand-50 text-brand-700'
                  : 'hover:bg-gray-50'
              }`}
            >
              <Folder className="w-3.5 h-3.5 text-gray-500 shrink-0" />
              {renamingId === f.folder_id ? (
                <>
                  <input
                    autoFocus
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRename(f.folder_id)
                      else if (e.key === 'Escape') { setRenamingId(null); setRenameDraft('') }
                    }}
                    className="flex-1 px-1 py-0.5 border border-gray-300 rounded text-xs min-w-0"
                  />
                  <button onClick={() => handleRename(f.folder_id)} className="p-0.5 text-green-600 hover:text-green-700" title="Save">
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => { setRenamingId(null); setRenameDraft('') }} className="p-0.5 text-gray-500 hover:text-gray-700" title="Cancel">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setFilter({ kind: 'folder', id: f.folder_id })}
                    className="flex-1 text-left truncate"
                  >
                    {f.name}
                  </button>
                  <span className="text-[10px] text-gray-400">{f.bookmark_count ?? 0}</span>
                  <button
                    onClick={() => { setRenamingId(f.folder_id); setRenameDraft(f.name) }}
                    className="p-0.5 text-gray-300 hover:text-gray-700 opacity-0 group-hover:opacity-100"
                    title="Rename"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleDeleteFolder(f)}
                    className="p-0.5 text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100"
                    title="Delete folder"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          ))}

          <div className="mt-2 pt-2 border-t">
            {newFolderMode ? (
              <div className="flex gap-1">
                <input
                  autoFocus
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateFolder()
                    else if (e.key === 'Escape') { setNewFolderMode(false); setNewFolderName('') }
                  }}
                  placeholder="Folder name…"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs min-w-0"
                />
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            ) : (
              <button
                onClick={() => setNewFolderMode(true)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-blue-700 hover:bg-blue-50"
              >
                <FolderPlus className="w-3.5 h-3.5" />
                <span>New folder</span>
              </button>
            )}
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            </div>
          ) : bookmarks.length === 0 ? (
            <div className="text-center py-16">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">
                {filter.kind === 'all' ? 'No bookmarks yet' : 'No bookmarks in this folder'}
              </p>
              <p className="text-gray-400 text-sm">
                Star articles in the search results to save them here
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                Go to Search
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-3">
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <Move className="w-3 h-3" />
                  <span>Click a chip below to move that bookmark to another folder. Click a star in the table to remove.</span>
                </div>
                <button
                  onClick={() => downloadCSV(articles, `bookmarks_${new Date().toISOString().split('T')[0]}.csv`)}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  Download CSV
                </button>
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                {bookmarks.map(b => {
                  const currentFolder = folders.find(f => f.folder_id === b.folder_id)
                  return (
                    <button
                      key={`mvbtn-${b.pmid}`}
                      ref={(el) => { moveButtonRefs.current[b.pmid] = el }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setMovePmid(b.pmid)
                        setMoveAnchor(e.currentTarget as HTMLElement)
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-gray-200 bg-white hover:bg-gray-50"
                      title={`Move PMID ${b.pmid}`}
                    >
                      <Move className="w-3 h-3" />
                      <span className="font-mono text-gray-500">{b.pmid}</span>
                      <span className="text-gray-400">→</span>
                      <span className="truncate max-w-[10rem]">{currentFolder ? currentFolder.name : 'Unsorted'}</span>
                    </button>
                  )
                })}
              </div>

              <ResultsTable
                articles={articles}
                onBookmarkToggle={handleBookmarkToggle}
              />
            </>
          )}
        </div>
      </div>

      <Popover
        anchor={moveAnchor}
        open={movePmid !== null}
        onClose={() => { setMovePmid(null); setMoveAnchor(null) }}
        align="left"
        width={240}
      >
        {movePmid && (
          <div className="text-sm">
            <div className="text-xs text-gray-500 px-2 pt-1 pb-2">Move to</div>
            <div className="max-h-56 overflow-y-auto">
              {folders.map(f => (
                <button
                  key={f.folder_id}
                  onClick={() => handleMove(movePmid, f.folder_id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
                >
                  <Folder className="w-3.5 h-3.5 text-gray-500" />
                  <span className="flex-1 truncate">{f.name}</span>
                </button>
              ))}
              <button
                onClick={() => handleMove(movePmid, null)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
              >
                <Folder className="w-3.5 h-3.5 text-gray-300" />
                <span className="flex-1 truncate italic text-gray-600">Unsorted</span>
              </button>
            </div>
          </div>
        )}
      </Popover>
    </div>
  )
}
